import * as React from 'react';
import {useState} from 'react';
import {default as axios} from 'axios';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import {Box} from "@mui/material";
import {EditorState, state, StateList} from '../main/config';
import Select from 'react-select';
import DefaultTable from "./defaultTable";
import {TextareaAutosize} from '@mui/material';
import NavBar from '../main/Components/NavigationBar/NavBar';
import Alert from "./alert";
import styles from './editor.module.scss';
import CheckBox from './Checkbox';
import {api} from '../utils/useAxios';

let editorState = state;

interface EditTopicProps {
    csvData: any[],
    data: any[],
    pickedStates: any[]
};

const EditTopic = (props: EditTopicProps): JSX.Element => {
    const {csvData, data, pickedStates} = props;
    const [topicList, setTopicList] = useState();
    const [selectedTopic, setSelectedTopic] = useState({value: "", label: ""});
    const [latestKey, setLatestKey] = useState(0);
    const [table, setTable] = useState({headers: {}, data: {}});
    const [description, setDescription] = useState("");
    const [lastUpdated, setLastUpdated] = useState("");
    const [deleteList, setDeleteList] = useState({});
    const [addList, setAddList] = useState({});
    const [isShowingAlert, setShowingAlert] = useState(EditorState.alert);
    const [checkBoxDisplay, setCheckBoxDisplay] = useState(false);
    const [topicIsActive, setTopicIsActive] = useState(false);

    //Alert helper function
    interface AlertType {
        send: boolean,
        isSubmit: boolean,
        message: string,
    };
    const setAlert = (alert: AlertType) => {
        setShowingAlert({...alert});
    };

    //Helper function for managing unique keys in a list
    const getNewKey = (latest: number): number => {
        let newKey = latest + 1;
        setLatestKey(newKey);
        return newKey;
    };

    const Checkbox = (): JSX.Element => {
        return <CheckBox 
            selectedTopic={selectedTopic.label}
            setTopicIsActive={setTopicIsActive}
            topicIsActive={topicIsActive}
        />
    };

    type DescriptionType = {
        desc: string
    };

    const Description = (props: DescriptionType): JSX.Element => {
        let {desc} = props;
        const [value, setValue] = useState(desc);
        return <TextareaAutosize
            onChange={(e) => { setValue(e.currentTarget.value); } }
            onBlur={(e) => { setDescription(e.currentTarget.value); } }
            value={value}
            placeholder={"Input description here..."}   />
    };

    type LastUpdatedType = {
        last: string
    };

    const LastUpdated = (props: LastUpdatedType): JSX.Element => {
        let {last} = props;
        const [value, setValue] = useState(last);
        return <TextareaAutosize
            className={styles.lastUpdated}
            onChange={(e) => { setValue(e.currentTarget.value); } }
            onBlur={(e) => { setLastUpdated(e.currentTarget.value); } }
            value={value}
            placeholder={"Last Updated..."}     />
    };

    //function to handle changes to text-area cells in the table
    const handleDataChange = (e, rowKey: string, headerKey: string): void => {
        let tempTable = table;
        tempTable.data[rowKey][headerKey] = e.currentTarget.value;
        setTable(tempTable);
    };

    //function to handle changes to headers
    const handleHeaderChange = (e, headerKey: number): void => {
        let delList = deleteList;
        let header = table.headers[headerKey];
        //Check to see if header key is already in the delete or add lists, if not, then insert into delete list
        if(!delList[headerKey] && !addList[headerKey]) {
            delList[headerKey] = header;
            setDeleteList(delList);
        };

        let tempTable = table;
        tempTable.headers[headerKey] = e.currentTarget.value;
        setTable(tempTable);
    };

    //function handle a column add: includes managing state for creating new rows in db
    const handleColumnAdd = ():void => {
        let tempTable = table;
        let newKey = getNewKey(latestKey);
        //Add the column to the AddList so it is not added another time on header value change
        let tempList = addList;
        tempList[newKey] = true;
        setAddList(tempList);
        tempTable.headers[newKey] = "";
        setTable(tempTable);
    };

    //function to handle column removal from tables: includes managing state for deleting rows in db
    const handleColumnRemove = (e):void => {
        let target = e.currentTarget.id;
        let tempTable = table;

        //modify delList to remove this column from the db if already contained in the db
        let delList = deleteList;
        delList[target] = tempTable.headers[target];
        setDeleteList(delList);

        delete tempTable.headers[target];
        let rows = Object.keys(tempTable.data);
        rows.forEach( row => {
            delete tempTable.data[row][target]
        })
        setTable({headers: tempTable.headers, data: {...tempTable.data}});
    };

    //DELETE row in table where row = {topic: $topic, header: $header}
    async function deleteTopicRow(row) {
        try {
            await api({
                method: 'post',
                url: 'deleteOpusRow',
                data: row,
            }).then( response => {
                console.log(response);
            })
        } catch (e) {
            if(e) console.log(e);
        }
    };

    //GET topic list for topic drop-down
    async function getTopicList() {
        try {
            await api({
                method: 'post',
                url: 'getOpusTopics',
                data: editorState.callTopics,
            }).then( response => {
                handleTopics(response);
            })
        } catch (e) {
            if(e) console.log(e);
        }
    };

    //GET data where rows are equal to TOPIC
    async function getData(option) {
        const response = await api({
            method: 'post',
            url: 'getOpusData',
            data: {query: 'SELECT * FROM opus WHERE topic=\'' + option.value + '\''}
        }).then(resp => {
            let columns = resp.data.Group;
            let headers = {}
            let tempData = {}
            let lastKey = 0

            columns.map((col, i) => {
                if(i === 0) {
                    setDescription(col["description"])
                    setLastUpdated(col["lastupdated"])
                }
                StateList.map( state => {
                    if(i === 0) {
                        tempData[state.value] = { "state": state.label }
                    }
                    tempData[state.value][i + 1] = col[state.value];
                })
                lastKey = i + 1
                return headers[i + 1] = col.header;
            })
            setLatestKey(lastKey);
            setTable({headers: headers, data: tempData});
            setTopicIsActive(resp.data.Group[0].isactive);
        })
        return response
    };

    //CREATE row WHERE topic = $topic, header = $header, data = associated column info
    async function sendToDatabase(data) {
        try {
            await api({
                method: 'post',
                url: 'upsertOpusData',
                data: data,
            }).then( response => {
                //console.log(response);
            })
        } catch (e) {
            if(e) console.log(e);
        }
    };

    //function to handle populating TOPICs to topic list
    const handleTopics = (response):void => {
        let tempTopics = response.data.topics.map(topic => {
            return topic
        });
        tempTopics.sort(function (a, b) {
            return a.localeCompare(b)
        });
        let tempList: any = [];
        tempTopics.forEach(topic => {
            let tempInsert = {
                value: topic,
                label: topic
            }
            tempList.push(tempInsert)
        });
        setTopicList(tempList);
    };

    //If topic list has not been set, pull topic list;
    if(!topicList) { 
        getTopicList(); 
    };

    //function to handle update or creation of new data in db
    const submitRows = ():void => {
        //Check each header value to ensure there is no empty strings
        let headerValues = Object.values(table.headers);
        //Ensure that all headers have some
        if(headerValues.indexOf("") !== -1) {
            setAlert({send: true, message: "Please Name All Column Headers", isSubmit: false});
        } else {
            //If there is items to delete, delete them first.
            if(deleteList) {
                let keys = Object.keys(deleteList);
                let tempList = deleteList;
                keys.forEach( key => {
                    deleteTopicRow({topic: selectedTopic.value, header: tempList[key]}).then( () => {
                        delete tempList[key];
                    });
                });
                setDeleteList(tempList);
            };
            //begin to submit updates to db using column keys
            let keys = Object.keys(table.headers);
            keys.map( key => {
                    let dataToSubmit = {
                        header: table.headers[key],
                        topic: selectedTopic.value,
                        description: description,
                        lastupdated: lastUpdated,
                        isactive: topicIsActive
                    }
                    StateList.map( state => {
                        let value = table.data[state.value][key];
                        if(!value) {
                            dataToSubmit[state.value] = "";
                        } else {
                            dataToSubmit[state.value] = value;
                        }
                    });
                    sendToDatabase(dataToSubmit).then(() => {
                        setAlert({send: true, message: "Topic Successfully Updated!", isSubmit: true})
                    });
                    setCheckBoxDisplay(false);
            });
        };
    };

    //Editor Page wrapper for <DefaultTable />
    return(
        <div>
            <NavBar
                csvData={csvData}
                data={data}
                pickedStates={pickedStates}
            />
            <Box className={styles.editor}
            sx={{ flexGrow: 1 }} 
            >
                <Grid container spacing={2} style={{ textAlign: "center"}}>
                    <Alert setAlert={setAlert} isShowingAlert={isShowingAlert} />
                    <Grid item xs={4}>
                        <div
                            className={styles.selectMenuLabel}
                           >
                                Topics
                        </div>
                        <Select
                            styles={{
                                // Fixes the overlapping problem of the component
                                menu: provided => ({...provided, zIndex: 9999, textAlign: "left"}),
                            }}
                            className={styles.selectMenu}
                            isSearchable
                            placeholder="Topics"
                            options={topicList}
                            onChange={(e:React.SetStateAction<{ value: string; label: string; }>) => {
                                setSelectedTopic(e);
                                setCheckBoxDisplay(false);
                            }}
                        />
                    </Grid>
                    <Grid item xs={1} className={styles.editSubmit}>
                        <Button className={styles.add} onClick={() => { 
                            if(selectedTopic){
                                console.log(getData(selectedTopic))
                                // setTopicIsActive(false);
                                setCheckBoxDisplay(true)

                                }}}>Edit</Button>
                    </Grid>
                    <Grid item xs={1} className={styles.editSubmit}>
                        <Button className={styles.submit} onClick={() => {submitRows()}}>Submit Topic</Button>
                    </Grid>
                    {/* Empty grid for spacing purposes */}
                    <Grid item xs />
                    <Grid item xs={2}>
                        <div className={styles.lastUpdatedLabel}>
                            Last Updated
                        </div>
                        <LastUpdated last={lastUpdated} />
                    </Grid>
                    {(checkBoxDisplay) ?    
                        <Grid item xs={12}>
                            <Checkbox />          
                        </Grid>
                     : null }
                    <Grid item xs={12}>
                        <div className={styles.descriptionLabel}>
                            Description
                        </div>
                        <div className={styles.description}>
                        <Description desc={description} />
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        <DefaultTable
                            table={table}
                            updateHeaders={handleHeaderChange}
                            updateData={handleDataChange}
                            addColumn={handleColumnAdd}
                            removeColumn={handleColumnRemove}
                        />
                    </Grid>
                </Grid>
            </Box>
        </div>
    )
};

export default EditTopic;